import React, { useEffect, useState, useCallback } from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { AccountService } from 'gw-capability-gateway-policy';
import { PolicyService } from 'gw-capability-gateway';

import MLClaimsReportAdditionalFilter from './MLClaimsReportAdditionalFilter';

import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLClaimsReportComponent.messages';
import metadata from './MLClaimsReportComponent.metadata.json5';

const MLClaimsReportComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history,
        viewModelService,
        convertLocalDateToDate
    } = props;

    const { gatewayParamConfig } = appConfig;
    const translator = useTranslator();

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const claimStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('ClaimStatus_ML');

    const loadClaimsData = useCallback(async (filteringData) => {
        updateIsLoading(true);            
        const response = isDashboard                
            ? await AccountService.getLimitedPoliciesClaimsForCurrentUser(gatewayParamConfig.jobsCreatedInLastXDays, rowLimit, filteringData, authHeader)                
            : await AccountService.getLimitedPoliciesClaimsForCurrentUser(gatewayParamConfig.jobsCreatedInLastXDays, 0, filteringData, authHeader) 

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, gatewayParamConfig.jobsCreatedInLastXDays, isDashboard, rowLimit]);

    useEffect(() => {
        loadClaimsData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilter = () => {
        loadClaimsData(filterData);
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const renderDateOfLossDateCell = useCallback((item) => {
        return item.lossDate ? convertLocalDateToDate(item.lossDate_ML) : '-';
    }, [convertLocalDateToDate]);

    const renderCustomerName = useCallback((item) => {
        if(_.isUndefined(item.displayName_ML)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber_ML}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder_ML))}
            </Link>
        )
    }, [getCustomerDisplayNameRevert]);

    const renderProductCell = useCallback((item) => {
        return item.product ? item.product : '-';
    }, []);

    const renderPolicyCell = useCallback((item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    }, []);

    const renderClaimCell = useCallback((item) => {
        if (_.isUndefined(item.claimNumber)) {
            return '-'
        }

        return (
            <Link to={`/policies/${item.policyNumber}/claims`} className={styles.linkCellValue}>
                {item.claimNumber}
            </Link>
        )
    }, []);

    const renderStatusCell = useCallback((item) => {
        if (_.isUndefined(item.status_ML)) {
            return '-'
        }
        const statusName = claimStatusTypelist.getCode(item.status_ML).name;
        const status = translator({
            id: statusName,
            defaultMessage: statusName
        });
        return _.upperCase(status);
    }, [claimStatusTypelist, translator]);

    const onViewAllButtonClicked = useCallback(() => {
        const reportView = 'claims-report';
        history.push('/activity-reports', {reportView : reportView} );
    }, [history]);

    const getMinDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30)
        return date;
    };

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        claimsReportResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            additionalFiltersComponent: MLClaimsReportAdditionalFilter,
            additionalFilterProps: {
                claimStatusTypelist
            },
            maxDate: new Date(),
            minDate: getMinDate()
        },
        claimsReportTable: {
            data: _.orderBy(filteredJobs, 'lossDate', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noRecentClaimActivityFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: translator(messages.claims)
        },
        viewAllClaimsButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_claims"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderDateOfLossDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderClaimCell,
            renderStatusCell,
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLClaimsReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired
}

export default withRouter(withViewModelService(MLClaimsReportComponent));
